import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Globe, PencilSimple, MagnifyingGlass, Sparkle,
  ArrowSquareOut, NotePencil, Lightning, DownloadSimple, Star, Robot, Code, Buildings,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail, API, fileUrl } from "@/lib/api";
import Layout, { STATUS_CONFIG } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import BusinessFormDialog from "@/components/BusinessFormDialog";
import ContentGenerator from "@/components/ContentGenerator";
import BulkAssist from "@/components/BulkAssist";
import AiReadiness from "@/components/AiReadiness";
import SchemaGenerator from "@/components/SchemaGenerator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_OPTIONS = ["not_started", "in_progress", "live", "rejected"];

export default function BusinessDetail() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [directories, setDirectories] = useState([]);
  const [citations, setCitations] = useState({});
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [genDir, setGenDir] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const isAutoSync = user?.subscription_plan === "auto_sync";

  const queueAll = async () => {
    setQueuing(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/citations/queue-all`);
      toast.success(`${data.queued} directories queued for our team`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setQueuing(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/businesses/${businessId}/citations/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(business?.name || "business").replace(/\s+/g, "_")}_citations.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    try {
      const [bRes, dRes, cRes] = await Promise.all([
        api.get(`/businesses/${businessId}`),
        api.get(`/directories`),
        api.get(`/businesses/${businessId}/citations`),
      ]);
      setBusiness(bRes.data);
      setDirectories(dRes.data);
      const map = {};
      cRes.data.forEach((c) => { map[c.directory_id] = c; });
      setCitations(map);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 404) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const patchCitation = async (dirId, changes) => {
    setCitations((prev) => ({ ...prev, [dirId]: { ...prev[dirId], directory_id: dirId, ...changes } }));
    try {
      const { data } = await api.patch(`/businesses/${businessId}/citations/${dirId}`, changes);
      setCitations((prev) => ({ ...prev, [dirId]: data }));
      if (changes.status || changes.listing_url !== undefined) refreshStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      load();
    }
  };

  const refreshStats = async () => {
    try {
      const { data } = await api.get(`/businesses/${businessId}`);
      setBusiness(data);
    } catch { /* ignore */ }
  };

  const categories = useMemo(() => {
    const set = [...new Set(directories.map((d) => d.category))];
    return set;
  }, [directories]);

  const filtered = useMemo(() => {
    return directories.filter((d) => {
      const c = citations[d.id] || { status: "not_started" };
      if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (categoryFilter !== "all" && d.category !== categoryFilter) return false;
      return true;
    });
  }, [directories, citations, search, statusFilter, categoryFilter]);

  const s = business?.stats || { progress: 0, live: 0, in_progress: 0, rejected: 0, not_started: 0, total: 0 };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="h-40 rounded-xl bg-slate-50 border border-slate-200 animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/dashboard")} data-testid="back-button"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        {/* Header */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4 min-w-0">
              {business.logo_path && (
                <img src={fileUrl(business.logo_path)} alt={`${business.name} logo`} data-testid="business-detail-logo"
                  className="h-16 w-16 rounded-xl border border-slate-200 object-contain bg-white shrink-0" />
              )}
              <div className="min-w-0">
                {business.category && <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF]">{business.category}</p>}
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1.5">{business.name}</h1>
                <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3 text-sm text-slate-600">
                  {(business.city || business.state) && <span className="flex items-center gap-1.5"><MapPin size={15} className="text-slate-400" />{[business.address, business.city, business.state, business.zip].filter(Boolean).join(", ")}</span>}
                  {business.phone && <span className="flex items-center gap-1.5"><Phone size={15} className="text-slate-400" />{business.phone}</span>}
                  {business.website && <span className="flex items-center gap-1.5"><Globe size={15} className="text-slate-400" />{business.website}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 shrink-0">
              <Button onClick={() => setSchemaOpen(true)} data-testid="schema-button" variant="secondary"
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                <Code size={16} className="mr-1.5" /> Schema
              </Button>
              <Button onClick={() => navigate(`/business/${businessId}/reviews`)} data-testid="reviews-button" variant="secondary"
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                <Star size={16} className="mr-1.5" /> Reviews
              </Button>
              <Button onClick={exportCsv} disabled={exporting} data-testid="export-csv-button" variant="secondary"
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                <DownloadSimple size={16} className="mr-1.5" /> {exporting ? "Exporting…" : "Export CSV"}
              </Button>
              <Button onClick={() => setEditOpen(true)} data-testid="edit-business-detail-button" variant="secondary"
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                <PencilSimple size={16} className="mr-1.5" /> Edit info
              </Button>
              <Button onClick={() => setBulkOpen(true)} data-testid="bulk-assist-button"
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
                <Lightning size={16} weight="fill" className="mr-1.5" /> Bulk Assist
              </Button>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="sm:col-span-1">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Progress</p>
              <p className="font-display text-3xl font-black text-slate-900 mt-1">{s.progress}%</p>
              <Progress value={s.progress} className="h-2 mt-2" />
            </div>
            <MiniStat label="Live" value={s.live} color="text-emerald-600" />
            <MiniStat label="In progress" value={s.in_progress} color="text-blue-600" />
            <MiniStat label="Rejected" value={s.rejected} color="text-red-600" />
            <MiniStat label="Not started" value={s.not_started} color="text-slate-500" />
          </div>
        </div>

        <AiReadiness businessId={businessId} refreshKey={s.live} />

        {isAutoSync && (
          <div data-testid="autosync-banner" className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Lightning weight="fill" size={22} className="text-[#0055FF] shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold text-slate-900">Auto-Sync is on — we submit for you</p>
                <p className="text-sm text-slate-600 mt-0.5">Queue your directories and our team handles the submissions. You can still track progress here anytime.</p>
              </div>
            </div>
            <Button onClick={queueAll} disabled={queuing} data-testid="queue-all-button"
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white shrink-0">
              {queuing ? "Queuing…" : "Queue all for our team"}
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="directory-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search directories…" className="pl-10 h-10" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-44 h-10" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-52 h-10" data-testid="category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Directory table */}
        <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="hidden lg:grid grid-cols-[1fr_180px_1fr_140px] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Directory</span>
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Status</span>
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Listing URL</span>
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 text-right">Actions</span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No directories match your filters.</div>
          ) : filtered.map((d) => {
            const c = citations[d.id] || { status: "not_started", listing_url: "", notes: "" };
            return (
              <div key={d.id} data-testid={`directory-row-${d.id}`}
                className="grid grid-cols-1 lg:grid-cols-[1fr_180px_1fr_140px] gap-3 lg:gap-4 px-6 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{d.name}</span>
                    {d.priority === 1 && <span className="text-[10px] font-bold uppercase tracking-wide text-[#0055FF] bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">Priority</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{d.category}</p>
                  {c.status === "live" && c.went_live_at && (
                    <p className="text-[11px] font-semibold text-emerald-600 mt-0.5" data-testid={`live-since-${d.id}`}>
                      Live since {new Date(c.went_live_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <Select value={c.status} onValueChange={(v) => patchCitation(d.id, { status: v })}>
                  <SelectTrigger data-testid={`status-select-${d.id}`} className="h-9 w-full lg:w-[170px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((st) => (
                      <SelectItem key={st} value={st}>
                        <span className="flex items-center gap-2">
                          <span className={`h-1.5 w-1.5 rounded-full ${STATUS_CONFIG[st].dot}`} />{STATUS_CONFIG[st].label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input data-testid={`listing-url-${d.id}`} defaultValue={c.listing_url || ""}
                  onBlur={(e) => { if (e.target.value !== (c.listing_url || "")) patchCitation(d.id, { listing_url: e.target.value }); }}
                  placeholder="https://listing-url…" className="h-9 text-sm" />

                <div className="flex items-center justify-start lg:justify-end gap-1.5">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={`notes-button-${d.id}`}
                        className={`h-9 w-9 rounded-md flex items-center justify-center border transition-colors ${c.notes ? "border-[#0055FF] text-[#0055FF]" : "border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}>
                        <NotePencil size={17} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72">
                      <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Notes</p>
                      <Textarea data-testid={`notes-input-${d.id}`} defaultValue={c.notes || ""} rows={4}
                        onBlur={(e) => { if (e.target.value !== (c.notes || "")) patchCitation(d.id, { notes: e.target.value }); }}
                        placeholder="Login used, status details…" />
                    </PopoverContent>
                  </Popover>

                  <a href={d.submit_url} target="_blank" rel="noreferrer" data-testid={`open-directory-${d.id}`}
                    className="h-9 w-9 rounded-md flex items-center justify-center border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                    <ArrowSquareOut size={17} />
                  </a>

                  <button onClick={() => setGenDir(d.id)} data-testid={`generate-content-${d.id}`}
                    className="h-9 w-9 rounded-md flex items-center justify-center bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors">
                    <Sparkle size={17} weight="fill" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <BusinessFormDialog open={editOpen} onOpenChange={setEditOpen} business={business} onSaved={(d) => setBusiness(d)} />
      <ContentGenerator businessId={businessId} directoryId={genDir} open={Boolean(genDir)}
        onOpenChange={(o) => !o && setGenDir(null)} />
      <BulkAssist open={bulkOpen} onOpenChange={setBulkOpen} business={business}
        directories={directories} citations={citations} onPatch={patchCitation} />
      <SchemaGenerator businessId={businessId} open={schemaOpen} onOpenChange={setSchemaOpen} />
    </Layout>
  );
}

const MiniStat = ({ label, value, color }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">{label}</p>
    <p className={`font-display text-3xl font-black mt-1 ${color}`}>{value}</p>
  </div>
);
